import React, { useState, useEffect } from 'react'
import styles from './styles'
import {
  Typography,
  InputAdornment,
  MenuItem,
  Stack,
  Tooltip,
  withStyles,
} from '@material-ui/core'
import { CoreButton, CoreTextField, CoreIconButton } from 'src/components/core'
import { Icon } from '@iconify/react'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import CollectionEdit from 'src/components/collection-edit'
import { messageBoxShowAction } from 'src/reducers/message-box/action'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import MenuPopover from 'src/components/MenuPopover'
import useTrackerFunction from 'src/hooks/useTrackerFunction'

const sortOptions = [
  { value: 'created_date_desc', label: 'Created Date (Newest)' },
  { value: 'created_date_asc', label: 'Created Date (Oldest)' },
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
]

const CollectionHeader = ({
  classes,
  onSearch,
  onSort,
  onRefresh,
  searchTerm = '',
  sortOption = 'created_date_desc',
}) => {
  const dispatch = useDispatch()
  const list = useSelector(state => state.collection.list)

  // Tracking function
  const trackNewCollection = useTrackerFunction('clicked_new_collection')

  const [search, setSearch] = useState(searchTerm)
  const [sortBy, setSortBy] = useState(sortOption)
  const [sortMenuOpen, setSortMenuOpen] = useState(null)

  // When props change, update local state
  useEffect(() => {
    setSearch(searchTerm)
  }, [searchTerm])

  useEffect(() => {
    setSortBy(sortOption)
  }, [sortOption])

  // Handle search input change
  const handleSearchChange = e => {
    const newSearch = e.target.value
    setSearch(newSearch)
    if (onSearch) onSearch(newSearch)
    if (onRefresh) onRefresh(newSearch, sortBy)
  }

  // Handle sort selection change
  const handleSortChange = value => {
    setSortBy(value)
    if (onSort) onSort(value)
    if (onRefresh) onRefresh(search, value)
    setSortMenuOpen(null)
  }

  const handleOpenSortMenu = event => {
    setSortMenuOpen(event.currentTarget)
  }

  const handleCloseSortMenu = () => {
    setSortMenuOpen(null)
  }

  const handleOpenCollectionEdit = () => {
    trackNewCollection()
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: 'New collection',
          draggable: false,
          maxWidth: '500px',
          component: (
            <CollectionEdit
              onRefresh={onRefresh}
              search={search}
              sortBy={sortBy}
            />
          ),
        }),
      ),
    )
  }

  const getCurrentSortLabel = () => {
    const option = sortOptions.find(opt => opt.value === sortBy)
    return option ? option.label : 'Sort by'
  }

  const collectionExists = list.length > 0

  return (
    <div className={classes.root}>
      <div className={classes.headerContainer}>
        <Typography className={classes.headerText}>Collections</Typography>
        {(collectionExists || searchTerm) && (
          <CoreButton
            startIcon={<Icon icon="mdi:plus" />}
            onClick={() => handleOpenCollectionEdit()}
          >
            New collection
          </CoreButton>
        )}
      </div>
      {(collectionExists || searchTerm) && (
        <div className={classes.searchContainer}>
          <CoreTextField
            className={classes.searchField}
            marginTop={false}
            placeholder="Search collections..."
            value={search}
            onChange={handleSearchChange}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="mdi:search" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CoreTextField
              className={classes.sortField}
              value={getCurrentSortLabel()}
              size="small"
              marginTop={false}
              onClick={handleOpenSortMenu}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    {sortMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </div>
      )}

      <MenuPopover
        open={Boolean(sortMenuOpen)}
        anchorEl={sortMenuOpen}
        onClose={handleCloseSortMenu}
        sx={{
          width: '200px',
          '& .MuiMenuItem-root': {
            px: 1,
            typography: 'body2',
            borderRadius: 0.75,
            marginBottom: '4px',
          },
        }}
        disabledArrow
      >
        {sortOptions.map(option => (
          <MenuItem
            key={option.value}
            onClick={() => handleSortChange(option.value)}
            selected={sortBy === option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </MenuPopover>
    </div>
  )
}

export default withStyles(styles)(CollectionHeader)
